import React, { useCallback, useEffect, useState } from "react";
import { adminApiRequest } from "../utils/adminApi";
import CategoryModal from "./CategoryModal";
import Modal from "./Modal";

export default function ShopCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await adminApiRequest({
        script: "list_shop_categories",
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load categories.");
      }

      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || "Unable to load categories.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const openCreateModal = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  const openDeleteModal = (category) => {
    setCategoryToDelete(category);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setCategoryToDelete(null);
  };

  const handleDelete = async (e) => {
    e.preventDefault();

    if (!categoryToDelete?.id) return;

    try {
      setDeleting(true);
      setError("");

      const res = await adminApiRequest({
        script: "delete_shop_category",
        data: {
          category_id: categoryToDelete.id,
        },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to delete category.");
      }

      setCategoryToDelete(null);
      await loadCategories();
    } catch (err) {
      setError(err.message || "Unable to delete category.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <section className="h-full min-h-0">
        <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">
                Shop Categories
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Create, edit and delete product categories.
              </p>
            </div>

            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              Add Category
            </button>
          </div>

          {error && (
            <div className="px-5 pt-4">
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto p-5">
            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                Loading categories...
              </div>
            ) : categories.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
                <p className="text-sm text-slate-500">No categories yet.</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left">
                    <thead className="bg-slate-50">
                      <tr className="text-sm text-slate-600">
                        <th className="px-4 py-3 font-semibold">Name</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-200 bg-white">
                      {categories.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-4 text-sm font-medium text-slate-900">
                            {item.name}
                          </td>

                          <td className="px-4 py-4 text-sm">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                                Number(item.is_active) === 1
                                  ? "bg-green-100 text-green-700"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {Number(item.is_active) === 1
                                ? "Active"
                                : "Inactive"}
                            </span>
                          </td>

                          <td className="px-4 py-4 text-sm">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => openEditModal(item)}
                                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                onClick={() => openDeleteModal(item)}
                                className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <CategoryModal
        isOpen={showModal}
        onClose={closeModal}
        onSaved={loadCategories}
        editingCategory={editingCategory}
      />

      {categoryToDelete && (
        <Modal
          title="Delete Category"
          onClose={closeDeleteModal}
          onSubmit={handleDelete}
          submitButtonText="Delete Category"
          showCancel={true}
          showSubmitButton={true}
          submitButtonClass="bg-red-600 hover:bg-red-700 text-white"
          disableSubmit={deleting}
        >
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-slate-900">
                {categoryToDelete.name}
              </span>
              ?
            </p>

            <p className="text-sm text-slate-500">
              This action cannot be undone.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}